import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { REGION_NAMES, regionByName } from "@/lib/regions";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import StoresModeration from "@/components/app/staff/StoresModeration";
import RegionAdverts from "@/components/app/staff/RegionAdverts";
import PayoutsCard from "@/components/app/staff/PayoutsCard";
import BrandingForm from "@/components/app/staff/BrandingForm";
import BannerApprovals from "@/components/app/staff/BannerApprovals";

const STATS = [
  ["stores_approved", "Approved stores"], ["stores_pending", "Pending"], ["markdowns_active", "Live adverts"],
  ["views", "Shopper views"], ["redemptions", "Redeemed in store"], ["region_followers", "Region followers"], ["store_followers", "Store followers"],
];

function StatsRow({ region, tick }) {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/admin/stats", { params: { region } }).then((r) => setS(r.data)).catch(() => {}); }, [region, tick]);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="op-stats">
      {STATS.map(([k, label]) => (
        <div key={k} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`op-stat-${k}`}>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
          <div className="font-display font-black text-2xl text-emerald-900">{s[k] ?? "—"}</div>
        </div>
      ))}
    </div>
  );
}

export default function OperatorPanel() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const isAdmin = user?.role === "admin";
  const [tick, setTick] = useState(0);
  const bump = () => setTick((t) => t + 1);
  const region = isAdmin ? (params.get("region") || REGION_NAMES[0]) : user?.operator_region;
  const slug = regionByName(region)?.slug;
  if (!region || !slug) return <div className="p-10 text-slate-500">No region assigned to this account.</div>;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <div className="text-xs font-black uppercase tracking-widest text-emerald-700">Regional dashboard</div>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight" data-testid="op-title">{region}</h1>
          {!isAdmin && <p className="text-slate-600 mt-1">Kia ora {user?.operator_name}. You manage everything in {region}.</p>}
        </div>
        {isAdmin && (
          <Select value={region} onValueChange={(v) => setParams({ region: v })}>
            <SelectTrigger className="w-56" data-testid="op-region-switch"><SelectValue /></SelectTrigger>
            <SelectContent>{REGION_NAMES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      <StatsRow region={region} tick={tick} />
      <PayoutsCard region={region} admin={isAdmin} />
      <div className="grid lg:grid-cols-2 gap-6">
        <StoresModeration region={region} onChange={bump} />
        <RegionAdverts region={region} tick={tick} onChange={bump} />
      </div>
      <BrandingForm slug={slug} name={region} />
      {!isAdmin && <BannerApprovals />}
    </div>
  );
}
